import type { ReactNode } from "react";
import { FileArchive, FileSpreadsheet, FileText, Mail, Paperclip, Upload } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { Link } from "react-router-dom";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { cn } from "@/lib/utils";
import { Container, MockFrame, TINTED_BAND } from "./primitives";

interface BandItem {
  id: string;
  title: string;
  line: string;
  /** An optional link after the line, to the page that says more. */
  link?: { to: string; label: string };
  label: string;
  mock: ReactNode;
}

/**
 * How documents get in and out, as three moments: a receipt uploaded or
 * e-mailed to the company's Zettl address and read by the AI, an older Fio statement imported, the month's bundle for the
 * accountant. Each is its own <section> with its own h2 and anchor.
 * Suppliers, file names and amounts are invented.
 */
export function DocumentsBand({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  const field = (k: string, v: string) => (
    <div className="flex items-baseline justify-between gap-3 py-1.5 text-[13px]">
      <span className="text-muted-foreground">{k}</span>
      <span className="break-words font-medium tabular-nums">{v}</span>
    </div>
  );

  const items: BandItem[] = [
    {
      id: "ucetenky",
      title: tr("Účtenky nahráním i e\u2011mailem", "Receipts by upload or e\u2011mail"),
      line: tr(
        "Přímo v Zettlu, nebo na adresu, kterou Zettl vytvoří vaší firmě.",
        "Right in Zettl, or to the address Zettl creates for your company."
      ),
      label: tr(
        "Dvě cesty, jak dostat účtenku do Zettlu: nahrání, nebo e-mail na adresu firmy. Pod nimi údaje, které z účtenky přečetla AI.",
        "Two ways to get a receipt into Zettl: upload, or e-mail to the company address. Below, the details the AI read from it."
      ),
      mock: (
        <div className="flex h-full flex-col justify-center p-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 text-[13px]">
            <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/40 bg-primary/[0.04] p-3 text-center">
              <Upload className="size-4 text-[hsl(var(--action))] dark:text-primary" />
              <span className="font-medium">{tr("Nahrát", "Upload")}</span>
              <span className="text-[11px] text-muted-foreground">PDF, JPG, PNG</span>
            </div>
            <span className="self-center text-xs text-muted-foreground">{tr("nebo", "or")}</span>
            <div className="flex flex-col items-center justify-center gap-1.5 rounded-lg border bg-background/60 p-3 text-center">
              <Mail className="size-4 text-[hsl(var(--action))] dark:text-primary" />
              <span className="font-medium">{tr("E-mailem", "By e-mail")}</span>
              <span className="text-[11px] text-muted-foreground">
                {tr("na adresu firmy", "to your company address")}
              </span>
            </div>
          </div>
          <p className="mt-2 flex items-center justify-center gap-1.5 rounded-md bg-secondary/60 px-2 py-1.5 font-mono text-[11px] dark:bg-secondary/40">
            <Mail className="size-3 text-muted-foreground" />
            moje-firma-17@zettl.cz
          </p>
          <div className="mt-3 rounded-lg border px-3 pb-1 pt-2">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-1.5 font-medium">
                <Paperclip className="size-3" /> uctenka-4471.pdf
              </span>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--action))] dark:text-primary">
                {tr("Přečteno AI", "Read by AI")}
              </span>
            </div>
            <div className="mt-1 divide-y">
              {field(tr("Dodavatel", "Supplier"), "Papírnictví Kotva")}
              {field(tr("Datum", "Date"), "12. 10. 2026")}
              {field(tr("Částka", "Amount"), "1 249,00 Kč")}
            </div>
          </div>
          <DotChip color="green" className="mt-3 text-foreground">
            {tr("Spárováno s platbou z 12. 10.", "Matched with the payment of 12 Oct")}
          </DotChip>
        </div>
      ),
    },
    {
      id: "gpc-abo",
      title: tr("Starší výpisy: import GPC a ABO", "Older statements: GPC and ABO"),
      line: tr(
        "Historii před napojením nahrajete z Fio, bez duplicit.",
        "Upload the history from before you connected, without duplicates."
      ),
      label: tr(
        "Nahrání výpisů GPC z Fio: dva soubory a výsledek importu",
        "Uploading GPC statements from Fio: two files and the import result"
      ),
      mock: (
        <div className="flex h-full flex-col justify-center p-4">
          <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-primary/40 bg-primary/[0.04] px-3 py-4 text-center">
            <Upload className="size-4 text-[hsl(var(--action))] dark:text-primary" />
            <p className="text-xs text-muted-foreground">
              {tr("Přetáhněte výpisy GPC sem", "Drop GPC statements here")}
            </p>
          </div>
          <div className="mt-3 divide-y rounded-lg border text-[13px]">
            {["vypis-2026-05.gpc", "vypis-2026-06.gpc"].map((f, i) => (
              <div key={f} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="flex min-w-0 items-center gap-2">
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="break-words font-mono text-xs">{f}</span>
                </span>
                <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                  {tr(`${i ? 23 : 19} plateb`, `${i ? 23 : 19} payments`)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <DotChip color="green" className="text-foreground">
              {tr("Importováno 38", "Imported 38")}
            </DotChip>
            <DotChip color="muted">
              {tr("Přeskočeno 4, už z Fio API", "Skipped 4, already from the Fio API")}
            </DotChip>
          </div>
        </div>
      ),
    },
    {
      id: "podklady-pro-ucetni",
      title: tr("Podklady pro účetní", "Files for your accountant"),
      line: tr(
        "Doklady za měsíc v jednom PDF, faktury v XLSX a ISDOC, doklady a platby pro POHODU.",
        "The month's receipts in one PDF, invoices as XLSX and ISDOC, documents and payments for POHODA."
      ),
      link: { to: "/for-accountants", label: tr("Pro účetní", "For accountants") },
      label: tr(
        "Balíček dokladů za září v PDF a exporty pro účetní",
        "The September receipts bundle as PDF and exports for the accountant"
      ),
      mock: (
        <div className="flex h-full flex-col justify-center p-4">
          <div className="relative mx-2 mt-2">
            {[2, 1].map((n) => (
              <div
                key={n}
                className="absolute inset-0 rounded-lg border bg-card shadow-sm"
                style={{ transform: `translate(${n * 6}px, ${n * -6}px) rotate(${n * 1.5}deg)` }}
              />
            ))}
            <div className="relative rounded-lg border bg-card p-3 shadow-md">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold">
                  <FileArchive className="size-4 text-[hsl(var(--dot-red))]" />
                  {tr("Doklady 09/2026.pdf", "Receipts 09/2026.pdf")}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {tr("18 dokladů", "18 receipts")}
                </span>
              </div>
              <div className="mt-2 divide-y text-xs">
                {[
                  ["Papírnictví Kotva", "1 249,00 Kč"],
                  ["Tiskárna Pixel", "2 890,00 Kč"],
                  ["Kavárna Na Rohu", "312,00 Kč"],
                ].map(([name, amount]) => (
                  <div key={name} className="flex justify-between gap-3 py-1">
                    <span className="break-words">{name}</span>
                    <span className="whitespace-nowrap font-mono tabular-nums">{amount}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-1.5">
            {["CSV", "XLSX", "ISDOC", "POHODA", tr("Výpis Fio · PDF", "Fio statement · PDF")].map(
              (x) => (
                <span
                  key={x}
                  className="inline-flex items-center gap-1 rounded-md border bg-background/60 px-2 py-1 text-xs font-medium"
                >
                  <FileSpreadsheet className="size-3 text-muted-foreground" />
                  {x}
                </span>
              )
            )}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-[13px]">
            <span className="flex items-center gap-2">
              <span className="flex size-6 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold">
                EK
              </span>
              {tr("Účetní · člen týmu", "Accountant · team member")}
            </span>
            <DotChip color="green">{tr("Přizvána", "Invited")}</DotChip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden [contain-intrinsic-size:auto_700px] [content-visibility:auto]",
        TINTED_BAND
      )}
    >
      {/* Each section spans three shared rows (mockup, title, line), so the frames
          are equally tall and the titles line up. */}
      <Container className="grid gap-x-6 gap-y-14 py-20 sm:py-28 md:grid-cols-3 md:grid-rows-[1fr_auto_auto] md:gap-y-0">
        {items.map((item) => (
          <section
            key={item.id}
            id={item.id}
            aria-labelledby={`${item.id}-title`}
            className="flex scroll-mt-28 flex-col md:row-span-3 md:grid md:scroll-mt-20 md:grid-rows-subgrid"
          >
            <MockFrame label={item.label} className="flex-1 md:h-full">
              {item.mock}
            </MockFrame>
            <h2
              id={`${item.id}-title`}
              className="mt-6 text-xl font-semibold leading-tight tracking-tight"
            >
              {item.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {item.line}
              {item.link && (
                <>
                  {" "}
                  <Link
                    to={localePath(locale, item.link.to)}
                    className="font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
                  >
                    {item.link.label} →
                  </Link>
                </>
              )}
            </p>
          </section>
        ))}
      </Container>
    </div>
  );
}
